// src/pages/Services.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const Services = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: "🛡️",
      title: "Prevention Programs",
      subtitle: "Community Awareness & Norm Transformation",
      description: "Comprehensive community awareness campaigns and social norm transformation initiatives to prevent SGBV among adolescent girls and young women.",
      features: [
        "Community dialogues and campaigns",
        "Challenging harmful gender norms",
        "Engaging men and boys as allies",
        "SGBV awareness promotion",
        "Social norm transformation"
      ],
      color: "var(--primary-blue)"
    },
    {
      icon: "🤝",
      title: "Protection Services",
      subtitle: "Safe Spaces & Support Systems",
      description: "Strengthened support systems, safe spaces, and referral pathways for survivors with comprehensive counseling, legal aid, and emergency health services.",
      features: [
        "Safe spaces for survivors",
        "Counseling services",
        "Legal aid support",
        "Emergency contraceptives",
        "PEP provision",
        "Referral directories"
      ],
      color: "var(--primary-teal)"
    },
    {
      icon: "💪",
      title: "Empowerment Initiatives",
      subtitle: "Skills Development & Mentorship",
      description: "Life skills training, vocational programs, and mentorship opportunities to equip young women with knowledge, skills, and resources.",
      features: [
        "Life skills training",
        "Vocational training programs",
        "Mentorship programs",
        "Leadership development",
        "Economic empowerment",
        "Decision-making skills"
      ],
      color: "var(--accent-orange)"
    },
    {
      icon: "🔗",
      title: "Multi-sector Coordination",
      subtitle: "Stakeholder Collaboration",
      description: "Foster collaboration among stakeholders to ensure cohesive SGBV response and sustainable community impact through coordinated efforts.",
      features: [
        "Healthcare provider training",
        "Educator capacity building",
        "Community leader engagement",
        "Peer educator programs",
        "Policy advocacy",
        "Partnership development"
      ],
      color: "var(--accent-yellow)"
    }
  ];

  const supportServices = [
    {
      icon: "🏥",
      title: "Healthcare Services",
      description: "Comprehensive health services including medical examination, treatment, and psychological support for survivors.",
      available: "24/7 Emergency Support"
    },
    {
      icon: "⚖️",
      title: "Legal Support",
      description: "Free legal aid, guidance through legal processes, and advocacy for survivors' rights and justice.",
      available: "Professional Legal Team"
    },
    {
      icon: "💬",
      title: "Counseling Services",
      description: "Individual and group counseling sessions, trauma therapy, and ongoing psychological support.",
      available: "Trained Counselors"
    },
    {
      icon: "📚",
      title: "Educational Support",
      description: "Educational assistance, school re-entry programs, and scholarships for affected young women.",
      available: "Scholarship Programs"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{
          background: 'linear-gradient(135deg, rgba(46, 134, 171, 0.9) 0%, rgba(68, 195, 164, 0.9) 100%), url("https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&h=800&fit=crop") center/cover',
          minHeight: '60vh'
        }}
      >
        <Container>
          <Row className="text-center text-white">
            <Col>
              <h1 
                style={{ 
                  fontSize: '4rem', 
                  fontWeight: '700', 
                  marginBottom: '30px',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                Our Services
              </h1>
              <p 
                style={{ 
                  fontSize: '1.4rem',
                  opacity: '0.9',
                  maxWidth: '800px',
                  margin: '0 auto'
                }}
              >
                Comprehensive programs designed to prevent, protect, and empower through evidence-based interventions
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Services */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 
                className="text-primary-blue mb-4"
                style={{ fontSize: '3rem', fontWeight: '700' }}
              >
                Core Service Areas
              </h2>
              <p 
                style={{ 
                  fontSize: '1.2rem', 
                  color: 'var(--text-gray)',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              >
                Four integrated service pillars addressing the multifaceted nature of SGBV
              </p>
            </Col>
          </Row>
          
          <Row>
            {services.map((service, index) => (
              <Col key={index} lg={6} className="mb-5">
                <Card 
                  className="card-custom h-100 shadow-hover"
                  style={{
                    transform: `translateY(${Math.sin((scrollY + index * 200) * 0.003) * 15}px)`,
                    borderTop: `5px solid ${service.color}`,
                    overflow: 'hidden'
                  }}
                >
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-start mb-3">
                      <div 
                        style={{
                          fontSize: '3rem',
                          marginRight: '20px',
                          flexShrink: 0
                        }}
                      >
                        {service.icon}
                      </div>
                      <div>
                        <h3 
                          className="text-primary-blue mb-2"
                          style={{ fontSize: '1.8rem', fontWeight: '600' }}
                        >
                          {service.title}
                        </h3>
                        <p 
                          style={{ 
                            color: service.color, 
                            fontWeight: '500',
                            fontSize: '1.1rem',
                            marginBottom: '15px'
                          }}
                        >
                          {service.subtitle}
                        </p>
                      </div>
                    </div>
                    
                    <p 
                      style={{ 
                        color: 'var(--text-gray)', 
                        lineHeight: '1.6',
                        marginBottom: '20px'
                      }}
                    >
                      {service.description}
                    </p>
                    
                    <h5 
                      className="text-primary-blue mb-3"
                      style={{ fontSize: '1.2rem', fontWeight: '600' }}
                    >
                      Key Features:
                    </h5>
                    <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                      {service.features.map((feature, idx) => (
                        <li 
                          key={idx}
                          style={{ 
                            color: 'var(--text-gray)', 
                            marginBottom: '8px',
                            lineHeight: '1.5'
                          }}
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      as={Link}
                      to="/contact"
                      className="btn-primary-custom"
                      style={{ width: '100%' }}
                    >
                      Learn More
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Support Services */}
      <section className="section-padding section-bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 
                className="text-primary-blue mb-4"
                style={{ fontSize: '3rem', fontWeight: '700' }}
              >
                Support Services
              </h2>
              <p 
                style={{ 
                  fontSize: '1.2rem', 
                  color: 'var(--text-gray)',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              >
                Comprehensive support systems for survivors and at-risk individuals
              </p>
            </Col>
          </Row>
          
          <Row>
            {supportServices.map((service, index) => (
              <Col key={index} lg={3} md={6} className="mb-4">
                <Card 
                  className="card-custom text-center p-4 h-100 shadow-hover"
                  style={{
                    transform: `translateY(${Math.sin((scrollY + index * 150) * 0.004) * 10}px)`
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '20px' }}>
                    {service.icon}
                  </div>
                  <h4 
                    className="text-primary-blue mb-3"
                    style={{ fontSize: '1.4rem', fontWeight: '600' }}
                  >
                    {service.title}
                  </h4>
                  <p 
                    style={{ 
                      color: 'var(--text-gray)', 
                      lineHeight: '1.6',
                      marginBottom: '20px'
                    }}
                  >
                    {service.description}
                  </p>
                  <div 
                    className="gradient-accent text-dark px-3 py-2 rounded-3"
                    style={{ fontSize: '0.9rem', fontWeight: '500' }}
                  >
                    {service.available}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 
                className="text-primary-blue mb-4"
                style={{ fontSize: '3rem', fontWeight: '700' }}
              >
                Our Service Process
              </h2>
              <p 
                style={{ 
                  fontSize: '1.2rem', 
                  color: 'var(--text-gray)',
                  maxWidth: '600px',
                  margin: '0 auto'
                }}
              >
                A systematic approach to delivering comprehensive SGBV prevention and response services
              </p>
            </Col>
          </Row>
          
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <div className="position-relative">
                {[
                  { step: "01", title: "Assessment & Identification", desc: "Community needs assessment and identification of at-risk individuals" },
                  { step: "02", title: "Service Delivery", desc: "Provision of appropriate services based on individual needs" },
                  { step: "03", title: "Follow-up & Support", desc: "Ongoing monitoring and continued support services" },
                  { step: "04", title: "Evaluation & Improvement", desc: "Regular evaluation and service improvement based on outcomes" }
                ].map((process, index) => (
                  <div key={index} className="d-flex align-items-start mb-4">
                    <div 
                      className="gradient-primary text-white rounded-3 d-flex align-items-center justify-content-center me-4"
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        fontSize: '1.2rem', 
                        fontWeight: '700',
                        flexShrink: 0
                      }}
                    >
                      {process.step}
                    </div>
                    <div>
                      <h4 
                        className="text-primary-blue mb-2"
                        style={{ fontSize: '1.3rem', fontWeight: '600' }}
                      >
                        {process.title}
                      </h4>
                      <p style={{ color: 'var(--text-gray)', lineHeight: '1.6', marginBottom: 0 }}>
                        {process.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={6}>
              <img
                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=500&fit=crop"
                alt="Service Process"
                className="img-fluid rounded-4 shadow-custom"
                style={{ width: '100%', height: '500px', objectFit: 'cover' }}
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-bg-primary text-white text-center">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2 
                style={{ 
                  fontSize: '3rem', 
                  fontWeight: '700', 
                  marginBottom: '30px' 
                }}
              >
                Need Our Services?
              </h2>
              <p 
                style={{ 
                  fontSize: '1.2rem', 
                  marginBottom: '40px',
                  opacity: '0.9'
                }}
              >
                Our services are available to all adolescent girls and young women in Rakai District. Contact us for support or to learn how you can get involved.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button 
                  as={Link} 
                  to="/contact" 
                  className="btn-accent-custom"
                  size="lg"
                >
                  Get Support
                </Button>
                <Button 
                  as={Link} 
                  to="/programs" 
                  variant="outline-light"
                  size="lg"
                  style={{
                    border: '2px solid rgba(255,255,255,0.8)',
                    borderRadius: '50px',
                    padding: '15px 30px'
                  }}
                >
                  View Programs
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Services;